import type { JSX } from 'preact';
import './CommentMarker.scss';

/**
 * Canonical comment marker — the small teardrop-shaped "bubble" that
 * anchors a comment to a piece of content. This is the visual that
 * lives on every page where PageLoop is mounted (marketing demo +
 * the real widget). Theme-aware via the `--lc-*` CSS variables in
 * `_comment-theme.scss`.
 *
 * Shape: rounded square with one pointed corner (bottom-left by
 * default) so it reads as a chat balloon pointing at the content
 * it's anchored to. Asymmetry is what gives it personality vs.
 * a plain circle.
 *
 * State:
 *   - `idle` — neutral resting glow.
 *   - `ping` — outward-radiating ring animation. Fires on new
 *     comments + when the user just authored a comment + manually
 *     when `ping={true}` is set by the caller (e.g. while a hover
 *     popover is open the marker stays "active").
 *
 * Theming: every dimension + colour reads from `--lc-marker-*` /
 * `--lc-ring-color`. Per-marker hue swaps via the `hue` prop set
 * `data-hue` which the SCSS picks up. Embedders who want to
 * override globally just set the vars on `[data-lc-theme='foo']`.
 */

export type CommentMarkerHue = 'violet' | 'cyan' | 'rose' | 'amber' | 'teal';

export interface CommentMarkerProps {
	/** Single letter shown inside the marker — first char of author's name. */
	letter: string;
	/** Built-in palette. Falls back to violet for unknown names; the
	 *  hash helper picks one stably per author elsewhere. */
	hue?: CommentMarkerHue;
	/** Animate the outward ring. Use for new comments + while
	 *  hovering/active. */
	ping?: boolean;
	/** Mark as the currently-focused marker (subtle scale+brightness). */
	active?: boolean;
	/** Free-positioning helper for floating instances. */
	style?: JSX.CSSProperties;
	/** Accessible label — usually `Comment by ${author}`. */
	ariaLabel?: string;
	onPointerEnter?: () => void;
	onPointerLeave?: () => void;
	onClick?: () => void;
}

export function CommentMarker(props: CommentMarkerProps) {
	const {
		letter,
		hue = 'violet',
		ping = false,
		active = false,
		style,
		ariaLabel,
		onPointerEnter,
		onPointerLeave,
		onClick,
	} = props;
	const cls =
		'lc-marker' +
		` lc-marker--${hue}` +
		(ping ? ' lc-marker--ping' : '') +
		(active ? ' lc-marker--active' : '');
	return (
		<button
			type="button"
			class={cls}
			style={style}
			aria-label={ariaLabel}
			onPointerEnter={onPointerEnter}
			onPointerLeave={onPointerLeave}
			onClick={onClick}
		>
			<span class="lc-marker__ring" aria-hidden="true" />
			<span class="lc-marker__letter">{letter.charAt(0).toUpperCase()}</span>
		</button>
	);
}

/**
 * Stable hue picker — hashes the author name to one of the five
 * preset hues so the same person is the same colour everywhere.
 */
export function hueFor(author: string): CommentMarkerHue {
	const hues: CommentMarkerHue[] = ['violet', 'cyan', 'rose', 'amber', 'teal'];
	let h = 0;
	for (let i = 0; i < author.length; i++) h = (h * 31 + author.charCodeAt(i)) | 0;
	return hues[Math.abs(h) % hues.length] ?? 'violet';
}
